import { HTMLAttributes } from 'react'

import Button from '@/components/base/button-more'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import HeroParallax from '@/components/base/hero-parallax'
import Media from '@/components/base/media'
import NewsCarousel, { type NewsCarouselCard } from '@/components/base/news-carousel'
import ScrollTo from '@/components/base/scroll-to'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useLoaderStore } from '@/store/loader'

import { useHeroMotion } from './motion/hero'
import { cn } from '@/lib/utils'
import { WPLink, WPMedia } from '@/types'

export type HomeHeroProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    media: WPMedia
    link: WPLink
    carousel: NewsCarouselCard[]
  }>

const HomeHero = ({ title, media, link, carousel, className, ...props }: HomeHeroProps) => {
  const { motionRef } = useHeroMotion()
  const setLoaderState = useLoaderStore((state) => state.setState)

  const handleLoad = () => {
    setLoaderState({ pageLoaded: true, loaded: true })
  }

  return (
    <Section ref={motionRef} className={cn('overflow-hidden', className)} {...props}>
      <Grid className="h-[112vh]">
        {media ? (
          <HeroParallax
            className={cn(
              'relative col-[1/-1] row-[1/-1] overflow-hidden',
              'after:absolute after:left-0 after:top-0 after:size-full after:bg-black/15'
            )}
          >
            <Media
              onLoad={handleLoad}
              className="object-cover"
              lazy={false}
              {...media}
              data-motion-media
            />
          </HeroParallax>
        ) : null}

        <Container className="h-svh-vh relative col-[1/-1] row-[1/-1] flex items-center justify-center">
          <Grid className="-mb-[5rem] gap-y-[2rem]">
            <div className="col-span-2 md:col-span-6 lg:col-span-12">
              {title ? (
                <Typography
                  variant="hero-tb-customized"
                  component="h1"
                  className={cn(
                    'text-center capitalize text-white',
                    'font-light [&_strong]:font-normal'
                  )}
                  data-motion-title
                >
                  {title}
                </Typography>
              ) : null}
            </div>
            {link && link.url ? (
              <div
                className={cn(
                  'col-span-2 md:col-span-6 lg:col-span-12',
                  'flex justify-center text-white'
                )}
              >
                <Button href={link?.url} target={link?.target} className="dark" data-motion-button>
                  {link?.title}
                </Button>
              </div>
            ) : null}
          </Grid>

          <ScrollTo
            className="absolute bottom-[5rem] left-1/2 hidden -translate-x-1/2 md:bottom-[6rem] lg:flex"
            data-motion-scroll
          />
        </Container>

        {carousel?.length ? (
          <div className="absolute right-1/2 top-0 h-[100svh] w-0 lg:right-0">
            <NewsCarousel
              collection={carousel || []}
              className={cn(
                'absolute bottom-[2rem] right-0 z-10 translate-x-1/2',
                'lg:right-[1rem] lg:translate-x-0'
              )}
              data-motion-carousel
            />
          </div>
        ) : null}
      </Grid>
    </Section>
  )
}

export default HomeHero
